import React from 'react';
import '../styles/components/SectionScoresBar.css';
import '../styles/common.css';
import PileItem from './common/PileItem';

// Make sure this interface matches what we defined in InteractionSearchList.tsx
interface ComponentSectionScore {
  name: string;
  score: number;
  maxScore: number;
}

interface SectionScoresBarProps {
  scores: ComponentSectionScore[];
  maxScore: number;
  viewMode?: 'bars' | 'piles';
}

const SectionScoresBar: React.FC<SectionScoresBarProps> = ({ scores, maxScore, viewMode = 'bars' }) => {
  // console.log("SectionScoresBar received scores:", scores);
  
  if (!scores || scores.length === 0) {
    // console.log("SectionScoresBar: No scores available");
    return <div className="section-scores-empty">No section data</div>;
  }

  // Colors for different sections with proper typing
  const sectionColors: { [key: string]: string } = {
    'Call Opening': '#4ade80', // Green
    'Customer Verification': '#ef4444', // Red
    'Problem Understanding': '#f59e0b', // Yellow/Orange
    'Troubleshooting': '#a3e635', // Lime
    'Escalation Handling': '#8b5cf6', // Purple
    'Satisfaction and Empathy': '#3b82f6', // Blue
    'Overall Call Handling': '#ec4899', // Pink
    // Add more sections as needed
  };

  if (viewMode === 'piles') {
    // Sort scores by percentage for top/bottom piles
    const sortedScores = [...scores].sort((a, b) => {
      const aPercentage = (a.score / a.maxScore) * 100;
      const bPercentage = (b.score / b.maxScore) * 100;
      return bPercentage - aPercentage;
    });

    const pilecount = 1;
    const topScores = sortedScores.slice(0, pilecount);
    const bottomScores = sortedScores.slice(-pilecount).reverse();

    return (
      <div className="section-scores-piles">
        <div className="piles-container">
          <div className="pile-column">
            <div className="pile-items">
              {topScores.map((section, index) => (
                <PileItem
                  key={index}
                  text={section.name}
                  score={`${section.score}/${section.maxScore}`}
                  icon="leaderboard"
                  thresholdHigh={80}
                  thresholdLow={60}
                  scoreValue={section.score}
                  maxScore={section.maxScore}
                  title={`${section.name}: ${section.score}/${section.maxScore}`}
                  numberStyle="threshold"
                />
              ))}
              {bottomScores.map((section, index) => (
                <PileItem
                  key={`bottom-${index}`}
                  text={section.name}
                  score={`${section.score}/${section.maxScore}`}
                  icon="trending_down"
                  thresholdHigh={80}
                  thresholdLow={60}
                  scoreValue={section.score}
                  maxScore={section.maxScore}
                  title={`${section.name}: ${section.score}/${section.maxScore}`}
                  numberStyle="threshold"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section-scores-bar">
      <div className="section-bars">
        {scores.map((section, index) => {
          const percentage = (section.score / section.maxScore) * 100;
          const color = sectionColors[section.name] || '#94a3b8'; // Default gray
          
          return (
            <div 
              key={index} 
              className="section-bar"
              style={{ 
                width: `${100 / scores.length}%`,
                height: `${Math.max(100, percentage)}%`
              }}
            >
              <div 
                className="section-bar-fill" 
                style={{ 
                  height: `${percentage}%`, 
                  backgroundColor: color 
                }}
                title={`${section.name}: ${section.score}/${section.maxScore}`}
              />
            </div>
          );
        })}
      </div>
      <div className="section-labels">
        {scores.map((section, index) => (
          <div key={index} className="section-label" style={{ width: `${100 / scores.length}%` }}>
            {section.name.substring(0, 3)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SectionScoresBar; 